import { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import { toast } from 'sonner';

/**
 * FavoriteButton - Heart icon to add/remove a scan from favorites.
 * 
 * Props:
 *   scanId: string - The scan ID to favorite/unfavorite
 *   size: 'sm' | 'md' | 'lg' - Button size
 *   showLabel: boolean - Show "Save" / "Saved" label
 */
export default function FavoriteButton({ scanId, size = 'md', showLabel = false }) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    checkFavoriteStatus();
  }, [scanId]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/favorites/check/${scanId}`, {
        withCredentials: true
      });
      setIsFavorite(response.data.is_favorite);
      setFavoriteId(response.data.favorite_id);
    } catch (e) {
      console.error('Failed to check favorite status:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (loading) return;
    
    // Haptic feedback
    if (navigator.vibrate) navigator.vibrate(20);
    
    // Trigger animation
    setAnimating(true);
    setTimeout(() => setAnimating(false), 300);
    
    try {
      if (isFavorite && favoriteId) {
        // Remove from favorites
        await axios.delete(`${API_URL}/favorites/${favoriteId}`, {
          withCredentials: true
        });
        setIsFavorite(false);
        setFavoriteId(null);
        toast.success('Removed from favorites');
      } else {
        // Add to favorites
        const response = await axios.post(
          `${API_URL}/favorites`,
          { scan_id: scanId },
          { withCredentials: true }
        );
        setIsFavorite(true);
        setFavoriteId(response.data.id);
        toast.success('Added to favorites!');
      }
    } catch (e) {
      console.error('Failed to toggle favorite:', e);
      if (e.response?.status === 400) {
        // Already favorited
        setIsFavorite(true);
      } else {
        toast.error('Could not update favorites');
      }
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (loading) {
    return (
      <button
        disabled
        data-testid="favorite-button"
        className={`${sizeClasses[size]} flex items-center justify-center rounded-full bg-qc-surface border border-qc-border opacity-50`}
      >
        <Heart className={`${iconSizes[size]} text-qc-text-faint`} />
      </button>
    );
  }

  return (
    <button
      onClick={toggleFavorite}
      data-testid="favorite-button"
      className={`
        ${showLabel ? 'px-4 gap-2' : sizeClasses[size]}
        flex items-center justify-center rounded-full
        ${isFavorite 
          ? 'bg-qc-red/15 border border-qc-red/30 text-qc-red' 
          : 'bg-qc-surface border border-qc-border text-qc-text-dim hover:text-qc-red hover:border-qc-red/30'
        }
        transition-all duration-200
        ${animating ? 'scale-125' : 'scale-100'}
      `}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
    >
      <Heart 
        className={`
          ${iconSizes[size]} 
          ${isFavorite ? 'fill-qc-red' : ''} 
          transition-transform duration-200
          ${animating ? 'scale-110' : 'scale-100'}
        `} 
      />
      {showLabel && (
        <span className="text-sm font-bold">
          {isFavorite ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}
